import type { useRenderToggleableContent } from '@ms/yammer-web-core-ui/dist/util/useRenderToggleableContent';
import { noop } from 'lodash';
import type { Context, FC, PropsWithChildren } from 'react';
import React, { createContext, useContext, useMemo, useState } from 'react';

export type PublisherType = 'POST' | 'QUESTION';

interface PostCreateModalContextProps {
  readonly showContent: () => void;
  readonly hideContent: () => void;
  readonly publisherType: PublisherType | null;
  readonly setPublisherType: (type: PublisherType | null) => void;
}

const defaultPostCreateModalContext: PostCreateModalContextProps = {
  showContent: noop,
  hideContent: noop,
  publisherType: null,
  setPublisherType: noop,
};

export const PostCreateModalContext: Context<PostCreateModalContextProps> =
  createContext<PostCreateModalContextProps>(defaultPostCreateModalContext);

export const useShowPostCreateModal = (): (() => void) =>
  useContext<PostCreateModalContextProps>(PostCreateModalContext).showContent;
export const useHidePostCreateModal = (): (() => void) =>
  useContext<PostCreateModalContextProps>(PostCreateModalContext).hideContent;
export const usePostCreateModalContext = (): PostCreateModalContextProps => useContext(PostCreateModalContext);

export interface CreatePostProviderProps {
  readonly usePostCreateModal: () => ReturnType<typeof useRenderToggleableContent>;
}

export const CreatePostProvider: FC<PropsWithChildren<CreatePostProviderProps>> = ({
  usePostCreateModal,
  children,
}) => {
  const [publisherType, setPublisherType] = useState<PublisherType | null>(null);
  const { showContent, hideContent, content } = usePostCreateModal();

  const postCreateModalContextValue: PostCreateModalContextProps = useMemo(
    () => ({ showContent, hideContent, publisherType, setPublisherType }),
    [showContent, hideContent, publisherType]
  );

  return (
    <PostCreateModalContext.Provider value={postCreateModalContextValue}>
      {children}
      {content}
    </PostCreateModalContext.Provider>
  );
};
